import type {
  Options,
  ContentTypes,
  ContentPage,
  ContentData,
  Locator,
  ContentTypeOptions,
} from '../utils/types'
import { fetchAPI } from '../utils/fetch'

class ClientCMS {
  private readonly options: Options
  private readonly url: string

  constructor({
    tenant,
    builder = 'faststore',
    workspace = 'master',
  }: Options) {
    this.options = { tenant, builder, workspace }
    this.url = this.getApiUrl()
  }

  public getOptions() {
    return this.options
  }

  public getApiUrl = () => {
    const { tenant, workspace } = this.options

    return `https://${workspace}--${tenant}.myvtex.com`
  }

  public fetch<T>(path = '', params?: URLSearchParams) {
    const { builder } = this.options

    const pathParams = params ? `?${params.toString()}` : ''

    return fetchAPI<T>(`${this.url}/_v/cms/api/${builder}/${path}${pathParams}`)
  }

  public getAllContentTypeIds = async (): Promise<string[]> => {
    const { contentTypes } = await this.fetch<ContentTypes>()

    return contentTypes.map((contentType) => contentType.id)
  }

  /**
   * pageId and versionId are used to fetch data for a specific page and version in the Vtex CMS.
   * Check: https://developers.vtex.com/docs/guides/faststore/headless-cms-previewing-changes-in-development-mode
   */
  public getCMSPagesByContentType = (
    contentType: string,
    {
      page = 1,
      perPage = 10,
      filters = {},
      versionId = '',
      pageId = '',
    }: Partial<ContentTypeOptions & { versionId: string; pageId: string }> = {}
  ) => {
    const params = new URLSearchParams()

    params.set('page', page.toString())
    params.set('per_page', perPage.toString())

    if (versionId) {
      params.set('versionId', versionId)
    }

    for (const [key, value] of Object.entries(filters)) {
      params.set(`filters[${key}]`, value)
    }

    return this.fetch<ContentPage>(`${contentType}/${pageId}`, params)
  }

  public getCMSPage = async (locator: Locator) => {
    const { documentId, contentType } = locator

    const params = new URLSearchParams()

    if (locator.releaseId) {
      params.set('releaseId', locator.releaseId)
    }

    if (locator.versionId) {
      params.set('versionId', locator.versionId)
    }

    return this.fetch<ContentData>(`${contentType}/${documentId}`, params)
  }
}

export default ClientCMS
